/**
 * Privacy Policy Page
 * GDPR compliant privacy policy for Artego Condo Manager
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, FileText } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="artego-prose min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-5 w-5" />
            <span>Torna alla Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            <span className="font-semibold text-slate-900">ARTEGO.APP</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-lg">
          <CardContent className="p-6 lg:p-10">
            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
              <p className="text-slate-600">Informativa sul trattamento dei dati personali</p>
              <p className="text-sm text-slate-500 mt-2">Ultimo aggiornamento: Gennaio 2026</p>
            </div>

            {/* Content */}
            <div className="prose prose-slate max-w-none">
              
              {/* Section 1 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                  Titolare del Trattamento
                </h2>
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="mb-2"><strong>ARTEGO SRLS</strong></p>
                  <p className="text-slate-600 text-sm">
                    Via M.se di Villabianca, 114<br />
                    90143 Palermo (PA) - Italia<br />
                    P.IVA: 07221580827<br />
                    Email: <a href="mailto:privacy@artegosrls.it" className="text-blue-600 hover:underline">privacy@artegosrls.it</a><br />
                    PEC: <a href="mailto:artegosrls@pec.it" className="text-blue-600 hover:underline">artegosrls@pec.it</a>
                  </p>
                </div>
                <p className="text-slate-700">
                  Artego SRLS, in qualità di Titolare del trattamento, La informa ai sensi dell'art. 13 del 
                  Regolamento UE 2016/679 ("GDPR") che i Suoi dati personali saranno trattati con le modalità 
                  e per le finalità seguenti.
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                  Tipologie di Dati Raccolti
                </h2>
                <p className="text-slate-700 mb-4">
                  La piattaforma ARTEGO.APP raccoglie le seguenti categorie di dati personali:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
                  <li><strong>Dati identificativi:</strong> nome, cognome, codice fiscale, indirizzo email, numero di telefono</li>
                  <li><strong>Dati di contatto:</strong> indirizzo di residenza, PEC, codice SDI</li>
                  <li><strong>Dati relativi al condominio:</strong> unità immobiliari, tabelle millesimali, quote condominiali</li>
                  <li><strong>Dati di navigazione:</strong> indirizzo IP, tipo di browser, pagine visitate, timestamp</li>
                  <li><strong>Dati di fatturazione:</strong> partita IVA, coordinate bancarie (per i pagamenti)</li>
                </ul>
              </section>

              {/* Section 3 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                  Finalità del Trattamento
                </h2>
                <p className="text-slate-700 mb-4">I Suoi dati personali sono trattati per le seguenti finalità:</p>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="font-semibold text-slate-900">a) Erogazione del servizio</h3>
                    <p className="text-slate-600 text-sm">Gestione dell'account, accesso alla piattaforma, funzionalità di gestione condominiale.</p>
                    <p className="text-xs text-slate-500 mt-1">Base giuridica: esecuzione del contratto (Art. 6.1.b GDPR)</p>
                  </div>
                  
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-semibold text-slate-900">b) Adempimenti fiscali e contabili</h3>
                    <p className="text-slate-600 text-sm">Emissione fatture, gestione pagamenti, adempimenti fiscali obbligatori.</p>
                    <p className="text-xs text-slate-500 mt-1">Base giuridica: obbligo legale (Art. 6.1.c GDPR)</p>
                  </div>
                  
                  <div className="border-l-4 border-amber-500 pl-4">
                    <h3 className="font-semibold text-slate-900">c) Comunicazioni di servizio</h3>
                    <p className="text-slate-600 text-sm">Notifiche relative al funzionamento del servizio, aggiornamenti, scadenze.</p>
                    <p className="text-xs text-slate-500 mt-1">Base giuridica: legittimo interesse (Art. 6.1.f GDPR)</p>
                  </div>
                  
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="font-semibold text-slate-900">d) Marketing (previo consenso)</h3>
                    <p className="text-slate-600 text-sm">Invio di newsletter, promozioni, offerte commerciali.</p>
                    <p className="text-xs text-slate-500 mt-1">Base giuridica: consenso (Art. 6.1.a GDPR)</p>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                  Periodo di Conservazione
                </h2>
                <p className="text-slate-700 mb-4">
                  I dati personali saranno conservati per i seguenti periodi:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-300 px-4 py-2 text-left">Tipologia di dato</th>
                        <th className="border border-slate-300 px-4 py-2 text-left">Periodo di conservazione</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-700">
                      <tr>
                        <td className="border border-slate-300 px-4 py-2">Dati dell'account</td>
                        <td className="border border-slate-300 px-4 py-2">Durata del rapporto contrattuale + 10 anni</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="border border-slate-300 px-4 py-2">Dati di fatturazione</td>
                        <td className="border border-slate-300 px-4 py-2">10 anni (obblighi fiscali)</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-300 px-4 py-2">Dati di navigazione</td>
                        <td className="border border-slate-300 px-4 py-2">24 mesi</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="border border-slate-300 px-4 py-2">Dati per marketing</td>
                        <td className="border border-slate-300 px-4 py-2">Fino a revoca del consenso</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 5 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
                  Diritti dell'Interessato
                </h2>
                <p className="text-slate-700 mb-4">
                  In conformità agli articoli 15-22 del GDPR, Lei ha diritto di:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Diritto di accesso</h3>
                    <p className="text-sm text-slate-600">Ottenere conferma del trattamento e accedere ai propri dati</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Diritto di rettifica</h3>
                    <p className="text-sm text-slate-600">Correggere dati inesatti o integrarli se incompleti</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Diritto alla cancellazione</h3>
                    <p className="text-sm text-slate-600">Richiedere la cancellazione dei dati ("diritto all'oblio")</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Diritto di limitazione</h3>
                    <p className="text-sm text-slate-600">Limitare il trattamento in determinati casi</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Diritto di opposizione</h3>
                    <p className="text-sm text-slate-600">Opporsi al trattamento per legittimo interesse</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Diritto alla portabilità</h3>
                    <p className="text-sm text-slate-600">Ricevere i dati in formato strutturato e leggibile</p>
                  </div>
                </div>
                <p className="text-slate-700 mt-4">
                  Per esercitare i propri diritti, è possibile contattare il Titolare all'indirizzo 
                  <a href="mailto:privacy@artegosrls.it" className="text-blue-600 hover:underline"> privacy@artegosrls.it</a>.
                </p>
              </section>

              {/* Section 6 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
                  Sicurezza dei Dati
                </h2>
                <p className="text-slate-700 mb-4">
                  Adottiamo misure tecniche e organizzative appropriate per proteggere i dati personali:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
                  <li>Crittografia SSL/TLS per tutte le comunicazioni</li>
                  <li>Crittografia dei dati a riposo (AES-256)</li>
                  <li>Accesso basato su ruoli e autenticazione a più fattori</li>
                  <li>Backup giornalieri con conservazione sicura</li>
                  <li>Monitoraggio continuo delle vulnerabilità</li>
                  <li>Formazione periodica del personale</li>
                </ul>
              </section>

              {/* Section 7 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">7</span>
                  Trasferimento Dati
                </h2>
                <p className="text-slate-700">
                  I dati sono conservati su server ubicati nell'Unione Europea. Nel caso di trasferimento 
                  verso paesi terzi, saranno adottate le garanzie previste dal GDPR (decisioni di adeguatezza, 
                  clausole contrattuali standard, ecc.).
                </p>
              </section>

              {/* Section 8 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">8</span>
                  Reclami
                </h2>
                <p className="text-slate-700">
                  Qualora ritenga che il trattamento dei Suoi dati violi il GDPR, ha diritto di proporre 
                  reclamo all'Autorità Garante per la Protezione dei Dati Personali:
                </p>
                <div className="bg-slate-50 rounded-lg p-4 mt-4">
                  <p className="text-slate-700">
                    <strong>Garante per la Protezione dei Dati Personali</strong><br />
                    Piazza Venezia, 11 - 00187 Roma<br />
                    Email: <a href="mailto:garante@gpdp.it" className="text-blue-600 hover:underline">garante@gpdp.it</a><br />
                    PEC: <a href="mailto:protocollo@pec.gpdp.it" className="text-blue-600 hover:underline">protocollo@pec.gpdp.it</a><br />
                    Sito web: <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.garanteprivacy.it</a>
                  </p>
                </div>
              </section>

              {/* Section 9 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">9</span>
                  Cookie Policy
                </h2>
                <p className="text-slate-700">
                  Per informazioni dettagliate sull'utilizzo dei cookie, La invitiamo a consultare la nostra 
                  <Link to="/cookie-policy" className="text-blue-600 hover:underline"> Cookie Policy</Link>.
                </p>
              </section>

              {/* Section 10 */}
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">10</span>
                  Modifiche alla Privacy Policy
                </h2>
                <p className="text-slate-700">
                  Il Titolare si riserva il diritto di modificare la presente informativa in qualsiasi momento. 
                  Le modifiche saranno pubblicate su questa pagina con indicazione della data di ultimo aggiornamento. 
                  Si consiglia di consultare periodicamente questa pagina.
                </p>
              </section>

            </div>

            {/* Footer Actions */}
            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-slate-500">
                <FileText className="h-5 w-5" />
                <span className="text-sm">Documento aggiornato a Gennaio 2026</span>
              </div>
              <div className="flex gap-3">
                <Link to="/cookie-policy">
                  <Button variant="outline">Cookie Policy</Button>
                </Link>
                <Link to="/login">
                  <Button>Accedi</Button>
                </Link>
              </div>
            </div>

          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-8 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2026 Artego SRLS - Tutti i diritti riservati</p>
          <p className="mt-1">Via M.se di Villabianca, 114 - 90143 Palermo</p>
        </div>
      </footer>
    </div>
  );
}
